/* eslint-disable import/no-unresolved */
/**
 * @fileoverview Hook UI Layer para obtener la lista de acciones rápidas del chat (QuickActionsCarouselMC).
 * Separa la lógica de UI de la presentación, permitiendo personalización y reutilización.
 *
 * @module useChatQuickActionsUI
 * @category Hook - UI Layer
 * @since 3.1.0
 * @version 3.1.0
 *
 * @typedef {Object} ChatQuickAction
 * @property {string} key - Clave única de la acción
 * @property {string} tooltip - Texto del tooltip
 * @property {function} [renderContent] - Renderiza el contenido visual del botón (tiene prioridad sobre imageSrc o icon)
 * @property {string|React.ElementType} [icon] - Icono (nombre o componente) a mostrar si no se provee renderContent ni imageSrc
 * @property {string} [imageSrc] - Ruta de imagen a mostrar si no se provee renderContent
 * @property {string} [className] - Clases adicionales para el botón
 * @property {function} [onClick] - Handler de click
 * @property {boolean} [divider] - Si es un divisor visual
 *
 * @param {Object} params
 * @param {function} params.openModal - Handler para abrir modales
 * @param {function} params.handleAttachMenuClick - Handler para abrir menú de adjuntos
 * @param {string} [params.platform] - Plataforma del hilo (p. ej. facebook_comments, instagram_comments)
 * @param {function} [params.onSendPdf] - Callback al enviar PDF desde el modal de pedido
 * @param {function} [params.onSendPaymentLink] - Callback al enviar link de pago desde el modal de pedido
 * @param {Object} [params.conversation] - Conversación seleccionada serializada (para asegurar cliente CRM antes de acciones)
 *
 * @returns {ChatQuickAction[]} Lista de acciones rápidas para el chat
 *
 * @example
 * const actions = useChatQuickActionsUI({ t, openModal });
 * <QuickActionsCarouselMC actions={actions} />
 *
 * @see QuickActionsCarouselMC
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FastIcon from 'images/fast.svg';
import ProductIcon from 'images/new_design/delivery-box.png';
import OrderIcon from 'images/order.svg';
import ReminderIcon from 'images/new_design/reminder-chat.svg';
import DealIcon from 'images/new_design/deal.svg';
import PaymentLinkIcon from 'images/new_design/payment_link.png';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';
import COMMENT_THREAD_PLATFORMS from '../constants/commentThreadPlatforms';
import {
  isTikTokCommentsPlatform,
  isTikTokPlatform
} from '../constants/platformMessageRestrictions';
import useEnsureInboxCustomer from '../actions/useEnsureInboxCustomer';
import useWhatsappIntegrationStatus from '../../../modules/Integrations/useWhatsappIntegrationStatus';
import { shouldShowWhatsappFlowsInboxQuickAction } from '../../../modules/WhatsappFlows/utils/whatsappFlowsSyncUtils';
// import NPSIcon from 'images/new_design/start.png';
// import MiaIcon from 'images/new_design/mia-suggestions-icon.png';

export default function useChatQuickActionsUI({
  openModal,
  handleAttachMenuClick,
  platform,
  onSendPdf,
  onSendPaymentLink,
  conversation
}) {
  const { t } = useTranslation('InboxMC');
  const { t: tInbox } = useTranslation('Inbox');

  const { customer } = useSelector((state) => state.inboxRightReducer);
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const { retailerInfoWithIntegrations } = useWhatsappIntegrationStatus(retailer_info);
  const retailerForWhatsappFlows = retailerInfoWithIntegrations ?? retailer_info;
  const showPaymentLink = retailer_info?.connected_to_wava;
  const { ensureInboxCustomer: ensureCustomer } = useEnsureInboxCustomer(conversation);

  const showWhatsappFlowsQuickAction = shouldShowWhatsappFlowsInboxQuickAction(
    platform,
    retailerForWhatsappFlows
  );

  return useMemo(() => {
    const baseButtonClass = 'send-icon-tooltip tw-mx-1 tw-flex tw-justify-center tw-items-center tw-h-[40px] tw-w-[39.78px] tw-bg-white tw-cursor-pointer';
    const borderButtonClass = 'tw-border tw-border-gray-200 tw-rounded-lg';
    const iconClassName = '!tw-size-[22px] tw-text-gray-700 hover:tw-text-blue-700 tw-transition-colors tw-duration-150 tw-cursor-pointer';
    const actions = [
      {
        key: 'attach',
        onClick: handleAttachMenuClick,
        iconClassName,
        tooltip: t('chat.attach'),
        text: t('chat.attach'),
        icon: 'attach-outline',
        className: baseButtonClass
      },
      { key: 'divider', divider: true },
      {
        key: 'fast',
        tooltip: t('chat.fast'),
        text: t('chat.fast'),
        imageSrc: FastIcon,
        className: `${baseButtonClass} ${borderButtonClass}`,
        onClick: async () => {
          const result = await ensureCustomer();
          if (!result.success) return;
          sharingInformationService.setSubject({
            openFastAnswersSelection: true
          });
          sharingInformationService.setSubject({});
        }
      },
      ...(showPaymentLink
        ? [
          {
            key: 'payment_link',
            tooltip: tInbox('sendPaymentLinks.sendPaymentLink'),
            text: tInbox('sendPaymentLinks.sendPaymentLink'),
            imageSrc: PaymentLinkIcon,
            className: `${baseButtonClass} ${borderButtonClass}`,
            onClick: () => openModal(
              {
                platform: platform || 'whatsapp',
                customer
              },
              'PaymentLinksModal'
            )
          }
        ]
        : []),
      {
        key: 'product',
        tooltip: t('chat.attach_product'),
        text: t('chat.attach_product'),
        imageSrc: ProductIcon,
        className: `${baseButtonClass} ${borderButtonClass}`,
        onClick: () => {
          sharingInformationService.setSubject({
            openProductSelection: true
          });
          sharingInformationService.setSubject({});
        }
      },
      {
        key: 'order',
        tooltip: t('chat.generate_order'),
        text: t('chat.generate_order'),
        imageSrc: OrderIcon,
        className: `${baseButtonClass} ${borderButtonClass}`,
        onClick: async () => {
          const result = await ensureCustomer();
          if (!result.success) return;
          openModal({
            onSendPdf,
            onSendPaymentLink,
            action: 'new'
          }, 'OrderModal', { className: "tw-w-[60vw]" });
        }
      },
      {
        key: 'reminder',
        tooltip: t('chat.generate_reminder'),
        text: t('chat.generate_reminder'),
        imageSrc: ReminderIcon,
        className: `${baseButtonClass} ${borderButtonClass}`,
        onClick: async () => {
          const result = await ensureCustomer();
          if (!result.success) return;
          openModal({
            hasCustomer: true,
            type: 'reminder'
          }, 'NewChat');
        }
      },
      {
        key: 'deal',
        tooltip: t('chat.generate_deal'),
        text: t('chat.generate_deal'),
        imageSrc: DealIcon,
        className: `${baseButtonClass} ${borderButtonClass}`,
        onClick: async () => {
          const result = await ensureCustomer();
          if (!result.success) return;
          openModal({
            externalCreate: true,
            deal: { customer: result.customer }
          }, 'ManageDeal');
        }
      },
      ...(showWhatsappFlowsQuickAction
        ? [
          {
            key: 'flows',
            tooltip: t('chat.whatsapp_flows'),
            text: t('chat.whatsapp_flows'),
            icon: 'whatsapp-fill-outline',
            iconClassName,
            className: `${baseButtonClass} ${borderButtonClass}`,
            onClick: () => openModal(
              {
                customer,
                platform,
                conversation
              },
              'WhatsappFlowsModal'
            )
          }
        ]
        : []),
      // {
      //   key: 'nps',
      //   tooltip: t('chat.generate_nps'),
      //   imageSrc: NPSIcon,
      //   className: `${baseButtonClass} ${borderButtonClass}`
      // },
      // {
      //   key: 'ai',
      //   tooltip: t('chat.generate_ai_answers'),
      //   imageSrc: MiaIcon,
      //   className: `${baseButtonClass} ${borderButtonClass}`,
      // },
      {
        key: 'add_activity',
        onClick: async () => {
          const result = await ensureCustomer();
          if (!result.success) return;
          const isMs = isInboxMsChat(conversation?.platform);
          openModal({ chatId: isMs ? conversation?.id : undefined }, 'CustomerEventSidebar');
        },
        iconClassName,
        tooltip: t('chat.add_activity'),
        text: t('chat.activity'),
        icon: 'note-outline',
        className: baseButtonClass
      }
    ];

    if (isTikTokCommentsPlatform(platform)) {
      return actions.filter((action) => ['attach', 'fast', 'product', 'deal', 'add_activity'].includes(action.key));
    }

    // Sin 'reminder': `Reminder` es un modelo de WhatsApp (whatsapp_template,
    // gupshup/karix_whatsapp_message) y lo despacha Reminders::WhatsappTemplates,
    // asi que no hay forma de agendar uno para TikTok.
    if (isTikTokPlatform(platform)) {
      return actions.filter((action) => ['attach', 'fast', 'product', 'deal', 'add_activity'].includes(action.key));
    }

    if (COMMENT_THREAD_PLATFORMS.has(platform)) {
      return actions.filter((action) => action.key === 'fast' || action.key === 'add_activity');
    }

    return actions;
  }, [
    t,
    tInbox,
    openModal,
    handleAttachMenuClick,
    platform,
    customer,
    showPaymentLink,
    showWhatsappFlowsQuickAction,
    ensureCustomer,
    onSendPdf,
    onSendPaymentLink,
    conversation
  ]);
}
